/* ============================================================
   DW Collections Index — search + Brands/Categories toggle + A–Z jump.
   Vanilla, no dependencies. Scoped per [data-dw-collections] so multiple
   instances (and theme-editor re-renders) are safe.
   ============================================================ */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.ciReady === '1') return;
    root.dataset.ciReady = '1';

    var searchInput = root.querySelector('[data-search]');
    var searchClear = root.querySelector('[data-search-clear]');
    var toggle = root.querySelector('[data-toggle]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-view]'));
    var azLinks = Array.prototype.slice.call(root.querySelectorAll('[data-az-link]'));
    var resultCount = root.querySelector('[data-result-count]');
    var noResults = root.querySelector('[data-no-results]');
    var noResultsTerm = root.querySelector('[data-no-results-term]');
    var controls = root.querySelector('[data-controls]');
    var azBar = root.querySelector('[data-az]');

    // Cache groups + their tiles once.
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-group]')).map(function (g) {
      return { el: g, letter: g.getAttribute('data-group'), tiles: Array.prototype.slice.call(g.querySelectorAll('[data-coltile]')) };
    });

    var query = '';
    var view = root.getAttribute('data-default-view') || 'all';

    /* ---------- Sticky offset measurement ----------
       The site header collapses on scroll. NOTE: .dw-header-wrap keeps a pinned
       full-height min-height (so collapsing never shifts the page — see
       dw-chrome.js), so its box height is NOT the visible chrome height. Measure
       the bottom edge of the actually-painted chrome (.dw-header, whose utility
       bar + nav collapse to zero) so our sticky bars sit flush under the compact
       header, not under the invisible reserved space below it. */
    var headerEl = document.querySelector('.dw-header');
    function measure() {
      var headH = headerEl ? Math.max(0, Math.round(headerEl.getBoundingClientRect().bottom)) : 0;
      var ctrlH = controls ? Math.round(controls.getBoundingClientRect().height) : 0;
      var azH = azBar ? Math.round(azBar.getBoundingClientRect().height) : 0;
      root.style.setProperty('--dw-ci-head', headH + 'px');
      root.style.setProperty('--dw-ci-controls', ctrlH + 'px');
      root.style.setProperty('--dw-ci-az', azH + 'px');
    }
    var measureQueued = false;
    function queueMeasure() {
      if (measureQueued) return;
      measureQueued = true;
      requestAnimationFrame(function () { measureQueued = false; measure(); });
    }
    measure();
    window.addEventListener('resize', queueMeasure, { passive: true });
    window.addEventListener('scroll', queueMeasure, { passive: true });

    /* ---------- Core filter ---------- */
    function applyFilter() {
      var q = query.trim().toLowerCase();
      var visible = 0;
      var liveLetters = {};

      for (var i = 0; i < groups.length; i++) {
        var grp = groups[i];
        var groupHasMatch = false;
        for (var j = 0; j < grp.tiles.length; j++) {
          var tile = grp.tiles[j];
          var typeOk = view === 'all' || tile.getAttribute('data-type') === view;
          var nameOk = q === '' || tile.getAttribute('data-name').indexOf(q) !== -1;
          var match = typeOk && nameOk;
          tile.hidden = !match;
          if (match) { groupHasMatch = true; visible++; }
        }
        grp.el.hidden = !groupHasMatch;
        if (groupHasMatch) liveLetters[grp.letter] = true;
      }

      // Dim A–Z letters whose group has nothing visible.
      for (var k = 0; k < azLinks.length; k++) {
        var link = azLinks[k];
        var letter = link.getAttribute('data-az-link');
        var avail = !!liveLetters[letter];
        link.classList.toggle('is-disabled', !avail);
      }

      // Result count + empty state.
      if (resultCount) {
        resultCount.textContent = visible + (visible === 1 ? ' collection' : ' collections');
      }
      if (noResults) {
        var empty = visible === 0;
        noResults.hidden = !empty;
        if (empty && noResultsTerm) {
          noResultsTerm.textContent = q !== '' ? '“' + query.trim() + '”' : 'this filter';
        }
      }
      if (searchClear) searchClear.hidden = q === '';
    }

    /* ---------- Search ---------- */
    if (searchInput) {
      var searchQueued = false;
      searchInput.addEventListener('input', function () {
        query = searchInput.value || '';
        if (searchQueued) return;
        searchQueued = true;
        requestAnimationFrame(function () { searchQueued = false; applyFilter(); });
      });
    }
    if (searchClear) {
      searchClear.addEventListener('click', function () {
        query = '';
        if (searchInput) { searchInput.value = ''; searchInput.focus(); }
        applyFilter();
      });
    }

    /* ---------- Brands / Categories toggle ---------- */
    function setView(next) {
      view = next;
      for (var i = 0; i < tabs.length; i++) {
        var active = tabs[i].getAttribute('data-view') === next;
        tabs[i].classList.toggle('is-active', active);
        tabs[i].setAttribute('aria-selected', active ? 'true' : 'false');
      }
      applyFilter();
    }
    if (toggle) {
      toggle.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-view]');
        if (tab) setView(tab.getAttribute('data-view'));
      });
    }

    /* ---------- A–Z jump (guard dimmed links) ---------- */
    for (var a = 0; a < azLinks.length; a++) {
      azLinks[a].addEventListener('click', function (e) {
        if (this.classList.contains('is-disabled')) { e.preventDefault(); }
      });
    }

    /* ---------- Scroll-spy: highlight the letter in view ---------- */
    if ('IntersectionObserver' in window && groups.length) {
      var spy = function () {
        var headH = parseInt(getComputedStyle(root).getPropertyValue('--dw-ci-head')) || 0;
        var ctrlH = parseInt(getComputedStyle(root).getPropertyValue('--dw-ci-controls')) || 0;
        var azH = parseInt(getComputedStyle(root).getPropertyValue('--dw-ci-az')) || 0;
        return headH + ctrlH + azH + 8;
      };
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var letter = entry.target.getAttribute('data-group');
          azLinks.forEach(function (l) {
            l.classList.toggle('is-current', l.getAttribute('data-az-link') === letter);
          });
        });
      }, { rootMargin: '-' + spy() + 'px 0px -65% 0px', threshold: 0 });
      groups.forEach(function (g) { io.observe(g.el); });
    }

    // Initial paint.
    setView(view);
  }

  function initAll() {
    document.querySelectorAll('[data-dw-collections]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-render support.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-dw-collections]');
    if (root) { root.dataset.ciReady = ''; init(root); }
  });
})();
